<template>
  <component
    :is="tagCompute"
    :id="id"
    :to="to"
    :href="hrefCompute"
    :disabled="disabled || loading"
    :loading="loading"
    class="button relative inline-flex justify-center uppercase font-medium text-s tracking-[1px] leading-normal"
    :class="[variantClass, sizeClass]"
    :target="target"
    :type="type"
    :aria-disabled="disabled || loading"
    :value="value"
    @click="clickEmitter"
    @focus="$emit('focus')"
  >
    <!-- focus emit is necessary for SearchComboBox to work -->
    <Spinner v-if="loading" />
    <BackIcon v-if="variant === 'back'" class="mr-8" />
    <slot name="left-icon"></slot>
    <span :class="[`${loading && 'invisible'}`]">
      {{ value }}
    </span>
    <slot></slot>
    <Arrow v-if="variant === 'arrow'" />
  </component>
</template>

<script lang="ts">
import Spinner from '../spinner/Spinner.vue';
import Arrow from '../../icons/ArrowPointer.vue';
import BackIcon from '../../icons/BackIcon.vue';

export default {
  name: 'Button',
  components: {
    Arrow,
    Spinner,
    BackIcon,
  },
  props: {
    value: {
      default: '',
      type: String,
    },
    id: {
      type: String,
      default: '',
    },
    size: {
      default: 'small',
      type: String,
    },
    target: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    variant: {
      default: 'primary',
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    loading: {
      default: false,
      type: Boolean,
    },
    href: {
      type: String,
      default: '',
    },
    preventDefault: {
      default: false,
      type: Boolean,
    },
    to: {
      type: String,
      default: '',
    },
  },
  computed: {
    hrefCompute(): String | undefined {
      if (this.target && !this.href) return '#';
      return this.href || undefined;
    },
    tagCompute(): String {
      if (this.href || this.target) return 'a';
      if (this.to) {
        return 'router-link';
      }
      return 'button';
    },
    variantClass(): String {
      const variantClasses = {
        primary: 'button-primary',
        secondary: 'button-secondary',
        outlined: 'button-outlined',
        text: 'button-text',
        arrow: 'button-arrow',
        back: 'button-back',
      };
      return variantClasses[this.variant];
    },
    sizeClass(): String {
      const sizeClasses = {
        small: 'button-small',
        medium: 'button-medium',
        large: 'button-large',
      };
      return sizeClasses[this.size];
    },
  },
  methods: {
    clickEmitter(e) {
      if (this.preventDefault) e.preventDefault();
      this.$emit('click', e);
    },
  },
};
</script>

<style lang="scss">
.button {
  align-items: center;
  font-family: 'Graphik Web', Helvetica, Arial, sans-serif;
  text-align: center;
  border-radius: theme('borderRadius.md');
  transition: all 100ms linear;
  outline: none;

  &:active {
    transform: scale(97%);
    box-shadow: 0px 0px 0px;
    transition: all 100ms linear;
  }

  &:focus-visible::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    border: 2px dotted theme('colors.brand.contrast');
    margin: -2px;
    border-radius: theme('borderRadius.md');
  }

  & .animate-spin {
    position: absolute;
  }

  &[loading] {
    &,
    &:hover,
    &:focus-visible,
    &[disabled] {
      color: transparent;
      pointer-events: none;
    }
  }

  &[disabled] {
    cursor: not-allowed;
  }
}
.button-small {
  padding: 0.34375rem 0.5rem;
}
.button-medium {
  padding: 0.594rem 1rem;
}
.button-large {
  padding: 1.125rem 1.5rem;
}
.button-primary {
  color: theme('colors.foreground');
  &:hover,
  &:focus-visible {
    background-color: theme('colors.brand.contrast');
    color: theme('colors.foreground');
  }
  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: 'none';
  }
  &,
  &[loading] {
    background-color: theme('colors.primary.DEFAULT');
    box-shadow: theme('boxShadow.sm');
  }
  svg path {
    fill: theme('colors.foreground');
  }
}
.button-secondary {
  color: theme('colors.primary.DEFAULT');
  border-color: theme('colors.grey.divider.contrast');
  border-style: solid;
  border-width: 0.75px;
  &:hover,
  &:focus-visible {
    color: theme('colors.white');
    border-color: theme('colors.brand.contrast');
    background-color: theme('colors.brand.contrast');
  }
  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: transparent;
    box-shadow: none;
  }
  &,
  &[loading] {
    background-color: theme('colors.foreground');
    box-shadow: theme('boxShadow.sm');
  }
  svg path {
    fill: theme('colors.brand.contrast');
  }
}
.button-outlined {
  border: theme('borderWidth.1') solid theme('colors.grey.divider.contrast');
  background-color: theme('colors.foreground');
  border-radius: theme('borderRadius.md');
  color: theme('colors.primary.DEFAULT');
  &:hover,
  &:focus-visible {
    border-color: theme('colors.brand.contrast');
    color: theme('colors.brand.contrast');
    svg: {
      color: theme('colors.brand.contrast');
    }
  }
  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
  }
  &,
  &[loading] {
    border: theme('borderWidth.1') solid theme('colors.grey.divider.contrast');
    background-color: theme('colors.foreground');
  }
}
.button-text {
  color: theme('colors.primary.DEFAULT');
  &:hover,
  &:hover svg path {
    color: theme('colors.brand.contrast');
    fill: theme('colors.brand.contrast');
  }
  &:not([disabled]),
  &[loading][disabled] {
    background-color: theme('colors.transparent');
    border: theme('borderWidth.1') solid transparent;
    padding-left: 0;
    padding-right: 0;
  }
  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
    border: theme('borderWidth.1') solid transparent;
  }
  svg path {
    fill: theme('colors.primary.DEFAULT');
  }
}
.button-arrow {
  color: theme('colors.primary.DEFAULT');
  &:hover {
    color: theme('colors.brand.contrast');
    .icon-arrow {
      transform: translateX(4px);
      transition: all 200ms linear;
      path: {
        fill: theme('colors.brand.contrast');
      }
    }
  }
  &[disabled] {
    color: theme('colors.grey.text.subdued');
    fill: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
  }
  &,
  &[loading] {
    background-color: theme('colors.transparent');
    border: theme('borderWidth.1') solid transparent;
    padding-left: 0;
    padding-right: 0;
  }
  .icon-arrow {
    margin-left: 8px;
    transition: all 200ms linear;
  }
  svg path {
    fill: theme('colors.primary.DEFAULT');
  }
}
.button-back {
  color: theme('colors.primary.DEFAULT');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  line-height: 1.5;
  text-transform: none;
  padding: 8px;
  &:hover,
  &:hover svg path {
    color: theme('colors.brand.contrast');
    fill: theme('colors.brand.contrast');
  }
  &:not([disabled]),
  &[loading][disabled] {
    background-color: theme('colors.transparent');
    border: theme('borderWidth.1') solid transparent;
    padding-left: 0;
    padding-right: 0;
  }
  &[disabled] {
    color: theme('colors.grey.text.subdued');
    background-color: theme('colors.grey.background.contrast');
    box-shadow: none;
    border: theme('borderWidth.1') solid transparent;
  }
  svg path {
    fill: theme('colors.primary.DEFAULT');
  }
}
</style>
